/**
 * PRNTS Homepage Customizer
 * Compacte versie van de product customizer voor de homepage
 * 
 * Features:
 * - Mapbox kaart integratie
 * - Locatie zoeken
 * - Stijl selectie
 * - Formaat/prijs selectie
 * - Direct toevoegen aan winkelwagen
 * - Ontvang locatie van hero section
 */

(function() {
  'use strict';

  const HomepageCustomizer = {
    map: null,
    marker: null,
    accessToken: null,
    currentLocation: {
      name: 'Etten-Leur',
      lat: 51.5733,
      lng: 4.6361
    },
    currentStyle: 'mapbox://styles/sander1971/clig9f069007501pf5qmi5tkr',
    currentFormat: '30x40',
    currentIcon: 'none',
    markerOffset: { lng: 0, lat: 0 },
    currentColor: '#E74C3C',
    currentOrientation: 'portrait',
    currentFrame: 'Met lijst',
    currentCountry: 'NEDERLAND',
    searchDebounceTimer: null,

    // DOM Elements cache
    elements: {},

    // Variant matrix: { '30x40__Geen': { id: 123, price: '€39,95' }, ... }
    variantMap: {},

    // Format dimensions for badge
    formatDimensions: {
      '21x30': '21 × 30 cm',
      '30x40': '30 × 40 cm',
      '50x70': '50 × 70 cm',
      '50×70': '50 × 70 cm'
    },

    // Format aspect ratios (width/height) for portrait
    formatAspectRatios: {
      '21x30': { portrait: '21 / 30', landscape: '30 / 21' },
      '30x40': { portrait: '30 / 40', landscape: '40 / 30' },
      '50x70': { portrait: '50 / 70', landscape: '70 / 50' },
      '50×70': { portrait: '50 / 70', landscape: '70 / 50' }
    },

    /**
     * Initialize
     */
    init() {

      const containers = document.querySelectorAll('[data-homepage-customizer]');
      const container = containers[0];
      if (!container) {
        return;
      }

      // Load variant matrix from embedded JSON
      try {
        const variantMapEl = document.querySelector('[data-variant-map]');
        if (variantMapEl) {
          const rawJson = variantMapEl.textContent;
          this.variantMap = JSON.parse(rawJson);
        }
      } catch (e) {
        // variant map parse error
      }

      // Cache elements
      this.cacheElements(container);

      // Read defaults from selected buttons BEFORE sizing
      this.initializeDefaults();

      // Setup poster sizing — JS computes exact pixel dimensions
      // matching PDF proportions: frame 4%, mat 7%, text 16.5%
      this.setupPosterSizing(container);

      // Apply initial frame visual state (met/zonder lijst)
      this.updatePosterFrame();

      // Get access token
      const mapContainer = container.querySelector('[data-customizer-map]');
      if (!mapContainer) {
        return;
      }

      this.accessToken = mapContainer.dataset.accessToken;

      // Set initial location from data attributes
      const defaultLat = parseFloat(mapContainer.dataset.defaultLat) || 51.5733;
      const defaultLng = parseFloat(mapContainer.dataset.defaultLng) || 4.6361;
      const defaultName = mapContainer.dataset.defaultLocation || 'Etten-Leur';

      this.currentLocation = {
        name: defaultName,
        lat: defaultLat,
        lng: defaultLng
      };

      // Bind events
      this.bindEvents();

      // Initialize available frames and variant from matrix (after defaults are set)
      this.updateAvailableFrames();
      this.updateVariantFromMatrix();

      // Initialize map when Mapbox is loaded
      this.waitForMapbox().then(() => this.initMap());

      // Listen for location from hero section
      this.listenForHeroLocation();

      // Sync hidden form inputs with initial defaults
      this.updateFormInputs();

    },

    /**
     * Initialize default values from selected buttons
     */
    initializeDefaults() {
      // Get default format from selected button OR first button
      let selectedFormat = this.elements.container.querySelector('.homepage-customizer__format.is-selected');
      if (!selectedFormat) {
        // Fallback: select first format button
        selectedFormat = this.elements.container.querySelector('.homepage-customizer__format');
        if (selectedFormat) {
          selectedFormat.classList.add('is-selected');
        }
      }

      if (selectedFormat) {
        this.currentFormat = selectedFormat.dataset.format;
        const variantId = selectedFormat.dataset.variantId;

        if (variantId && this.elements.variantInput) {
          this.elements.variantInput.value = variantId;
        }
      }

      // Get default style from selected button OR first button
      let selectedStyle = this.elements.container.querySelector('.homepage-customizer__style.is-selected');
      if (!selectedStyle) {
        selectedStyle = this.elements.container.querySelector('button[data-style]');
        if (selectedStyle) selectedStyle.classList.add('is-selected');
      }
      if (selectedStyle) {
        this.currentStyle = selectedStyle.dataset.styleUrl;
        this.currentStyleName = selectedStyle.dataset.style;
      }

      // Get default color from selected button OR first button
      let selectedColor = this.elements.container.querySelector('.homepage-customizer__color.is-selected');
      if (!selectedColor) {
        selectedColor = this.elements.container.querySelector('.homepage-customizer__color');
        if (selectedColor) selectedColor.classList.add('is-selected');
      }
      if (selectedColor) {
        this.currentColor = selectedColor.dataset.color;
      }

      // Get default icon from selected button OR first button
      let selectedIcon = this.elements.container.querySelector('.homepage-customizer__icon.is-selected');
      if (!selectedIcon) {
        selectedIcon = this.elements.container.querySelector('.homepage-customizer__icon');
        if (selectedIcon) selectedIcon.classList.add('is-selected');
      }
      if (selectedIcon) {
        this.currentIcon = selectedIcon.dataset.icon;
      }

      // Get default orientation from selected button OR first button
      let selectedOrientation = this.elements.container.querySelector('.homepage-customizer__orientation.is-selected');
      if (!selectedOrientation) {
        selectedOrientation = this.elements.container.querySelector('.homepage-customizer__orientation');
        if (selectedOrientation) selectedOrientation.classList.add('is-selected');
      }
      if (selectedOrientation) {
        this.currentOrientation = selectedOrientation.dataset.orientation;
      }

      // Get default frame from selected button OR first button
      let selectedFrame = this.elements.container.querySelector('.homepage-customizer__frame.is-selected');
      if (!selectedFrame) {
        selectedFrame = this.elements.container.querySelector('.homepage-customizer__frame');
        if (selectedFrame) selectedFrame.classList.add('is-selected');
      }
      if (selectedFrame) {
        this.currentFrame = selectedFrame.dataset.frame;
      }
    },

    /**
     * Cache DOM elements
     */
    cacheElements(container) {
      this.elements = {
        container,
        mapContainer: container.querySelector('[data-customizer-map]'),
        mapWrapper: container.querySelector('[data-map-wrapper]'),
        searchInput: container.querySelector('[data-location-search]'),
        searchResults: container.querySelector('[data-search-results]'),
        textInput: container.querySelector('[data-text-input]'),
        countryInput: container.querySelector('[data-country-input]'),
        textOverlay: container.querySelector('[data-map-text]'),
        styleButtons: container.querySelectorAll('button[data-style]'),
        formatButtons: container.querySelectorAll('[data-format]'),
        formatBadgeSize: container.querySelector('[data-format-size]'),
        formatBadgeDims: container.querySelector('[data-format-dims]'),
        priceDisplay: container.querySelector('[data-price-display]'),
        form: container.querySelector('[data-customizer-form]'),
        variantInput: container.querySelector('[data-variant-id]'),
        locationInput: container.querySelector('[data-location-name]'),
        coordsInput: container.querySelector('[data-coordinates]'),
        textValueInput: container.querySelector('[data-text-value]'),
        styleValueInput: container.querySelector('[data-style-value]'),
        formatValueInput: container.querySelector('[data-format-value]'),
        iconValueInput: container.querySelector('[data-icon-value]'),
        colorValueInput: container.querySelector('[data-color-value]'),
        orientationValueInput: container.querySelector('[data-orientation-value]'),
        iconButtons: container.querySelectorAll('[data-icon]'),
        colorButtons: container.querySelectorAll('[data-color]'),
        orientationButtons: container.querySelectorAll('[data-orientation]'),
        frameButtons: container.querySelectorAll('[data-frame]'),
        frameValueInput: container.querySelector('[data-frame-value]'),
        posterTitle: container.querySelector('.homepage-customizer__poster-title'),
        posterCountry: container.querySelector('.homepage-customizer__poster-country'),
        posterCoords: container.querySelector('.homepage-customizer__poster-coords'),
        poster: container.querySelector('.homepage-customizer__poster'),
        previewUrlInput: container.querySelector('[data-preview-url]'),
        customizationJson: container.querySelector('[data-customization-json]')
      };

    },

    /**
     * Wait for Mapbox to load
     */
    waitForMapbox() {
      return new Promise((resolve) => {
        if (typeof mapboxgl !== 'undefined') {
          resolve();
          return;
        }
        
        const checkMapbox = setInterval(() => {
          if (typeof mapboxgl !== 'undefined') {
            clearInterval(checkMapbox);
            resolve();
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkMapbox);
          console.warn('Mapbox failed to load');
        }, 10000);
      });
    },

    /**
     * Initialize Mapbox map
     */
    initMap() {
      if (!this.accessToken || !this.elements.mapContainer) {
        console.error('Mapbox access token or container missing');
        return;
      }

      mapboxgl.accessToken = this.accessToken;

      const zoom = parseInt(this.elements.mapContainer.dataset.zoom) || 13;

      this.map = new mapboxgl.Map({
        container: this.elements.mapContainer.id,
        style: this.currentStyle,
        center: [this.currentLocation.lng, this.currentLocation.lat],
        zoom: zoom,
        attributionControl: false,
        preserveDrawingBuffer: true
      });

      // Add initial marker using updateMarkerIcon
      this.map.on('load', () => {
        this.updateMarkerIcon();
        this.updatePosterText();
      });

      // Click on map to select location
      this.map.on('click', (e) => {
        const { lng, lat } = e.lngLat;
        this.setLocation(lng, lat);
        this.reverseGeocode(lng, lat);
      });

      // Handle style change - re-add marker after style loads
      this.map.on('style.load', () => {
        this.updateMarkerIcon();
      });
    },

    /**
     * Bind event listeners
     */
    bindEvents() {
      // Location search
      if (this.elements.searchInput) {
        this.elements.searchInput.addEventListener('input', (e) => {
          clearTimeout(this.searchDebounceTimer);
          this.searchDebounceTimer = setTimeout(() => {
            this.searchLocation(e.target.value);
          }, 300);
        });

        this.elements.searchInput.addEventListener('focus', () => {
          if (this.elements.searchResults.children.length > 0) {
            this.elements.searchResults.hidden = false;
          }
        });

        // Close results on click outside
        document.addEventListener('click', (e) => {
          if (!this.elements.searchInput.contains(e.target) && 
              !this.elements.searchResults.contains(e.target)) {
            this.elements.searchResults.hidden = true;
          }
        });
      }

      // Text input
      if (this.elements.textInput) {
        this.elements.textInput.addEventListener('input', (e) => {
          const text = e.target.value.toUpperCase();
          // Update poster title
          if (this.elements.posterTitle) {
            this.elements.posterTitle.textContent = text || this.currentLocation.name.toUpperCase();
          }
          this.updateFormInputs();
        });
      }

      // Country/subtitle input — updates poster country text live
      if (this.elements.countryInput) {
        this.elements.countryInput.addEventListener('input', (e) => {
          const country = e.target.value.toUpperCase();
          if (this.elements.posterCountry) {
            this.elements.posterCountry.textContent = country ? `\u2014 ${country} \u2014` : '';
          }
          this.updateFormInputs();
        });
      }

      // Style buttons
      if (this.elements.styleButtons && this.elements.styleButtons.length > 0) {
        this.elements.styleButtons.forEach(btn => {
          btn.addEventListener('click', () => {
            this.elements.styleButtons.forEach(b => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');

            const styleUrl = btn.dataset.styleUrl;
            const styleName = btn.dataset.style;
            this.currentStyle = styleUrl;
            this.currentStyleName = styleName;

            // Update map wrapper dataset
            if (this.elements.mapWrapper) {
              this.elements.mapWrapper.dataset.style = styleName;
            }

            // Change map style
            if (this.map) {
              this.map.setStyle(styleUrl);
              // Marker will be re-added by style.load event in initMap
            }

            // Lookup correct variant from matrix (format + frame + style)
            this.updateVariantFromMatrix();

            this.updateFormInputs();
          });
        });
      }

      // Format buttons
      this.elements.formatButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          this.elements.formatButtons.forEach(b => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');

          const format = btn.dataset.format;
          const variantId = btn.dataset.variantId;
          const price = btn.dataset.price;

          this.currentFormat = format;


          // Update poster aspect ratio based on orientation
          this.updatePosterAspectRatio();

          // Update available frames for this format
          this.updateAvailableFrames();

          // Lookup correct variant from matrix (format + frame)
          this.updateVariantFromMatrix();

          this.updateFormInputs();
        });
      });

      // Frame/Lijst buttons
      if (this.elements.frameButtons && this.elements.frameButtons.length > 0) {
        this.elements.frameButtons.forEach(btn => {
          btn.addEventListener('click', () => {
            if (btn.disabled) return;
            this.elements.frameButtons.forEach(b => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');

            this.currentFrame = btn.dataset.frame;

            // Visually toggle frame on poster preview
            this.updatePosterFrame();

            // Lookup correct variant from matrix (format + frame)
            this.updateVariantFromMatrix();

            this.updateFormInputs();
          });
        });
      }

      // Orientation buttons
      if (this.elements.orientationButtons && this.elements.orientationButtons.length > 0) {
        this.elements.orientationButtons.forEach(btn => {
          btn.addEventListener('click', () => {
            this.elements.orientationButtons.forEach(b => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');

            this.currentOrientation = btn.dataset.orientation;
            this.updatePosterAspectRatio();
            this.updateFormInputs();
          });
        });
      }

      // Form submit - sends variant ID + ALL customization properties
      if (this.elements.form) {
        this.elements.form.addEventListener('submit', async (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation(); // Prevent theme.js from also handling this

          // Block cart operations in Shopify Theme Editor (they return 422)
          if (window.Shopify && window.Shopify.designMode) {
            this.showError('Winkelwagen werkt niet in de Theme Editor. Gebruik de Preview link om te testen.');
            return;
          }

          // Make sure all form inputs reflect current state
          this.updateFormInputs();

          // Generate preview image URL from current map state
          const previewUrl = this.generatePreviewUrl();

          // Validate
          const validation = this.validateFormData();
          if (!validation.valid) {
            this.showError(validation.errors.join(', '));
            return;
          }

          // Get variant ID (as integer for Shopify JSON API)
          const rawVariantValue = this.elements.variantInput?.value;
          const variantId = parseInt(rawVariantValue, 10);

          if (!variantId || isNaN(variantId)) {
            this.showError('Selecteer een formaat');
            return;
          }

          // Build properties directly from JS state (most reliable)
          const { lat, lng, name } = this.currentLocation;
          const text = this.elements.textInput?.value || name.toUpperCase();
          const country = this.elements.countryInput?.value?.toUpperCase() || this.currentCountry || 'NEDERLAND';
          const styleName = this.getStyleName(this.currentStyle);
          const iconLabels = {
            'none': '', 'heart': 'Hartje', 'home': 'Thuis', 'star': 'Ster',
            'baby': 'Baby', 'ring': 'Verloving', 'cake': 'Verjaardag',
            'airplane': 'Reis', 'coffee': 'Eerste date', 'graduation': 'Afstuderen',
            'pawprint': 'Huisdier', 'music': 'Concert', 'pin': 'Pin'
          };
          const colorLabels = {
            '#E74C3C': 'Rood', '#1A1A1A': 'Zwart', '#3498DB': 'Blauw',
            '#27AE60': 'Groen', '#F39C12': 'Oranje', '#9B59B6': 'Paars'
          };
          const orientationLabels = { 'portrait': 'Staand', 'landscape': 'Liggend' };

          const properties = {
            'Locatie': name,
            'Coordinaten': `${lat.toFixed(6)},${lng.toFixed(6)}`,
            'Tekst': text,
            'Ondertitel': country,
            'Kaartstijl': styleName,
            'Formaat': this.currentFormat,
            'Lijst': this.currentFrame || 'Geen',
            'Orientatie': orientationLabels[this.currentOrientation] || this.currentOrientation
          };

          // Only add icon + color if an icon is selected (color is for the marker icon)
          const iconLabel = iconLabels[this.currentIcon] || '';
          if (iconLabel) {
            properties['Icoon'] = iconLabel;
            properties['Kleur'] = colorLabels[this.currentColor] || this.currentColor;
          }

          // Add preview URL (underscore prefix = hidden in cart display, used for order)
          if (previewUrl) {
            properties['_preview_url'] = previewUrl;
          }

          // Add complete customization data for printer PDF generation
          // (underscore prefix = hidden from customer, accessible in order admin)
          // Marker position (may differ from location center if user dragged it)
          const markerLat = lat + (this.markerOffset?.lat || 0);
          const markerLng = lng + (this.markerOffset?.lng || 0);

          const customizationData = {
            location: { name, lat, lng },
            marker: { lat: markerLat, lng: markerLng },
            text: text,
            country: country,
            style: styleName,
            styleId: this.currentStyle.split('/').pop(),
            styleUrl: this.currentStyle,
            format: this.currentFormat,
            frame: this.currentFrame || 'Geen',
            orientation: this.currentOrientation,
            icon: this.currentIcon !== 'none' ? this.currentIcon : null,
            color: this.currentIcon !== 'none' ? this.currentColor : null,
            colorLabel: this.currentIcon !== 'none' ? (colorLabels[this.currentColor] || this.currentColor) : null,
            zoom: this.map?.getZoom() || 13,
            mapContainerWidth: this.map?.getContainer()?.offsetWidth || null,
            mapBounds: this.map ? this.map.getBounds().toArray() : null,
            mapboxToken: this.accessToken,
            timestamp: new Date().toISOString()
          };
          properties['_customization_data'] = JSON.stringify(customizationData);

          // Show loading state
          const submitBtn = this.elements.form.querySelector('[type="submit"]');
          const originalText = submitBtn?.innerHTML;
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.innerHTML = '<span>Toevoegen...</span>';
          }

          try {
            // Use JSON items[] format (modern Shopify cart API - most reliable)
            const payload = {
              items: [{
                id: variantId,
                quantity: 1,
                properties: properties
              }]
            };

            const payloadJson = JSON.stringify(payload);

            // Use proper Shopify route (handles locale/market prefixes)
            const cartAddUrl = window.PRNTS?.routes?.cart_add || '/cart/add.js';

            let response;
            try {
              response = await fetch(cartAddUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                body: payloadJson,
                credentials: 'same-origin'
              });
            } catch (networkError) {
              throw networkError;
            }

            // Parse response - handle non-JSON
            let data;
            const rawText = await response.text();
            try {
              data = JSON.parse(rawText);
            } catch (parseError) {
              throw new Error('Server returned invalid JSON response');
            }

            // Retry once on 422 — may be stale session or editor context
            if (!response.ok && response.status === 422) {
              await new Promise(r => setTimeout(r, 500));

              response = await fetch(cartAddUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                body: payloadJson,
                credentials: 'same-origin'
              });

              const retryText = await response.text();
              try {
                data = JSON.parse(retryText);
              } catch (e) {
                throw new Error('Retry: server returned invalid JSON');
              }
            }

            if (!response.ok) {
              throw new Error(data.description || data.message || 'Kon niet toevoegen aan winkelwagen');
            }

            // Success feedback
            if (submitBtn) {
              submitBtn.innerHTML = '<span>Toegevoegd! ✓</span>';
              submitBtn.classList.add('is-success');
            }

            // Open cart drawer instead of redirect
            await this.openCartDrawer();

            // Reset button after delay
            setTimeout(() => {
              if (submitBtn) {
                submitBtn.innerHTML = originalText;
                submitBtn.disabled = false;
                submitBtn.classList.remove('is-success');
              }
            }, 2000);

          } catch (error) {

            // Reset button
            if (submitBtn) {
              submitBtn.innerHTML = originalText;
              submitBtn.disabled = false;
            }

            // Show user-friendly error message
            let msg = error.message || 'Er ging iets mis bij het toevoegen aan de winkelwagen';
            if (msg.includes('uitverkocht')) {
              msg = 'Product kon niet worden toegevoegd. Probeer de pagina te vernieuwen en het opnieuw.';
            }
            this.showError(msg);
          }

        });
      }

      // Icon buttons
      this.colorGroup = document.querySelector('[data-color-group]');
      if (this.elements.iconButtons && this.elements.iconButtons.length > 0) {
        this.elements.iconButtons.forEach(btn => {
          btn.addEventListener('click', () => {
            this.elements.iconButtons.forEach(b => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');

            this.currentIcon = btn.dataset.icon;
            this.toggleColorVisibility();
            this.updateMarkerIcon();
            this.updateFormInputs();
          });
        });
        // Initial state: hide color if default icon is 'none'
        this.toggleColorVisibility();
      }

      // Color buttons
      if (this.elements.colorButtons && this.elements.colorButtons.length > 0) {
        this.elements.colorButtons.forEach(btn => {
          btn.addEventListener('click', () => {
            this.elements.colorButtons.forEach(b => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');

            this.currentColor = btn.dataset.color;
            this.updateMarkerIcon();
            this.updateFormInputs();
          });
        });
      }

    },

    /**
     * PDF-exact poster proportions — single source of truth
     * These match generate-order-pdf.js line for line
     */
    PDF_PROPORTIONS: {
      framePadRatio: 0.040,  // shortSide * 4%
      matPadRatio:   0.070,  // shortSide * 7%
      textRatio:     0.165,  // 16.5% of innerH
      fadeRatio:     0.35,   // 35% of mapH
      titleFsRatio:  0.22,   // textH * 22%
      countryFsRatio:0.10,   // textH * 10%
      coordsFsRatio: 0.09,   // textH * 9%
      gapRatio:      0.055,  // textH * 5.5%
    },

    setupPosterSizing(container) {
      const preview = container.querySelector('.homepage-customizer__preview');
      const poster = container.querySelector('.homepage-customizer__poster');
      const frame = container.querySelector('.homepage-customizer__poster-frame');
      const mat = container.querySelector('.homepage-customizer__poster-mat');
      const posterInner = container.querySelector('.homepage-customizer__poster-inner');
      if (!preview || !poster || !frame || !mat) return;

      let lastW = 0, lastH = 0;
      let debounceTimer = null;

      const doSize = () => {
        const isMobile = window.innerWidth <= 768;
        const availW = preview.clientWidth;
        const availH = preview.clientHeight;
        if (availW <= 0) return;

        // Get aspect ratio from format (matches PDF FORMATS)
        const format = this.currentFormat || '30x40';
        const orient = this.currentOrientation || 'portrait';
        const ratioStr = (this.formatAspectRatios[format] || {})[orient] || '3 / 4';
        const parts = ratioStr.split('/').map(s => parseFloat(s.trim()));
        const ratio = parts[0] / parts[1]; // width / height

        let w, h;
        if (isMobile) {
          // Mobile: fill entire width
          w = availW;
          h = w / ratio;
        } else {
          // Desktop/tablet: fit within preview area
          h = availH;
          w = h * ratio;
          if (w > availW) {
            w = availW;
            h = w / ratio;
          }
        }

        w = Math.floor(w);
        h = Math.floor(h);

        if (w === lastW && h === lastH) return;
        lastW = w;
        lastH = h;

        poster.style.width = w + 'px';
        poster.style.height = h + 'px';

        // PDF-exact padding: shortSide * ratio
        const shortSide = Math.min(w, h);
        const withFrame = this.currentFrame === 'Met lijst';
        const framePad = withFrame ? shortSide * this.PDF_PROPORTIONS.framePadRatio : 0;
        const matPad = shortSide * this.PDF_PROPORTIONS.matPadRatio;

        frame.style.padding = framePad + 'px';
        mat.style.padding = matPad + 'px';

        // After layout settles, compute text font sizes from innerH
        requestAnimationFrame(() => {
          if (!posterInner) return;
          const innerH = posterInner.clientHeight;
          if (innerH <= 0) return;

          const p = this.PDF_PROPORTIONS;
          const textH = innerH * p.textRatio;
          posterInner.style.setProperty('--pi-title-fs', (textH * p.titleFsRatio) + 'px');
          posterInner.style.setProperty('--pi-country-fs', (textH * p.countryFsRatio) + 'px');
          posterInner.style.setProperty('--pi-coords-fs', (textH * p.coordsFsRatio) + 'px');
          posterInner.style.setProperty('--pi-gap', (textH * p.gapRatio) + 'px');

          // Resize map after everything is set
          if (this.map) this.map.resize();
        });
      };

      const debouncedSize = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(doSize, 100);
      };

      this._sizePoster = () => {
        lastW = 0; lastH = 0;
        doSize();
      };

      const ro = new ResizeObserver(debouncedSize);
      ro.observe(preview);
      // Run immediately and once more after fonts/map load
      doSize();
      requestAnimationFrame(doSize);
    },

    /**
     * Update poster aspect ratio based on format and orientation
     */
    updatePosterAspectRatio() {
      if (this._sizePoster) {
        this._sizePoster();
      }
    },

    /**
     * Visually toggle frame/mat on poster based on currentFrame
     * "Met lijst" = black frame + white mat (default)
     * "Zonder lijst" = no frame, no mat, subtle shadow
     */
    updatePosterFrame() {
      const container = this.elements.container;
      if (!container) return;
      const poster = container.querySelector('.homepage-customizer__poster');
      if (!poster) return;

      const withFrame = this.currentFrame === 'Met lijst';
      poster.classList.toggle('homepage-customizer__poster--frameless', !withFrame);

      // Re-run sizing since padding changes affect layout
      if (this._sizePoster) this._sizePoster();
    },

    /**
     * Disable frame buttons that have no variant for the current format
     */
    updateAvailableFrames() {
      if (!this.elements.frameButtons || !this.variantMap) return;

      const availableFrames = [];
      // Check which frames have at least one variant for the current format
      for (const key of Object.keys(this.variantMap)) {
        const [format, frame] = key.split('__');
        if (format === this.currentFormat && !availableFrames.includes(frame)) {
          availableFrames.push(frame);
        }
      }

      let currentFrameAvailable = false;

      this.elements.frameButtons.forEach(btn => {
        const frame = btn.dataset.frame;
        const isAvailable = availableFrames.includes(frame);
        btn.disabled = !isAvailable;
        btn.classList.toggle('is-unavailable', !isAvailable);

        if (isAvailable && frame === this.currentFrame) {
          currentFrameAvailable = true;
        }
      });

      // If current frame not available, auto-select first available
      if (!currentFrameAvailable) {
        const fallback = availableFrames.includes('Zonder lijst') ? 'Zonder lijst' : availableFrames[0];
        if (fallback) {
          this.currentFrame = fallback;
          this.elements.frameButtons.forEach(b => b.classList.remove('is-selected'));
          const fallbackBtn = this.elements.container.querySelector(`[data-frame="${fallback}"]`);
          if (fallbackBtn) fallbackBtn.classList.add('is-selected');
          this.updatePosterFrame();
        }
      }
    },

    /**
     * Lookup variant ID + price from the variant matrix based on current format + frame + style
     * Matrix key: "FORMAT__FRAME__STYLE" e.g. "30x40__Zonder lijst__klassiek"
     */
    updateVariantFromMatrix() {
      const key = `${this.currentFormat}__${this.currentFrame}__${this.currentStyleName}`;
      const entry = this.variantMap[key];

      if (entry) {
        if (this.elements.variantInput) {
          this.elements.variantInput.value = entry.id;
        }
        if (this.elements.priceDisplay) {
          this.elements.priceDisplay.textContent = entry.price;
        }
      } else {
        const formatBtn = this.elements.container?.querySelector(`.homepage-customizer__format.is-selected`);
        if (formatBtn) {
          const fallbackId = formatBtn.dataset.variantId;
          const fallbackPrice = formatBtn.dataset.price;
          if (this.elements.variantInput) this.elements.variantInput.value = fallbackId;
          if (this.elements.priceDisplay) this.elements.priceDisplay.textContent = fallbackPrice;
        }
      }
    },

    /**
     * Update poster text (title, country, coords)
     */
    updatePosterText() {
      const { lat, lng, name } = this.currentLocation;
      
      // Update title
      if (this.elements.posterTitle) {
        const text = this.elements.textInput?.value || name.toUpperCase();
        this.elements.posterTitle.textContent = text;
      }
      
      // Update coordinates
      if (this.elements.posterCoords) {
        const latDir = lat >= 0 ? 'N' : 'S';
        const lngDir = lng >= 0 ? 'E' : 'W';
        const latDeg = Math.floor(Math.abs(lat));
        const latMin = Math.floor((Math.abs(lat) - latDeg) * 60);
        const lngDeg = Math.floor(Math.abs(lng));
        const lngMin = Math.floor((Math.abs(lng) - lngDeg) * 60);
        
        const coordsText = `${latDeg}°${latMin}'${latDir} ${lngDeg}°${lngMin}'${lngDir}`;
        this.elements.posterCoords.textContent = coordsText;
      }
    },

    /**
     * Update marker icon on map
     */
    updateMarkerIcon() {
      if (!this.map) return;
      
      // Remove old marker
      if (this.marker) {
        this.marker.remove();
      }
      
      // Icon SVG paths
      const iconSVGs = {
        'none': '', // No icon, just default marker
        'heart': '<svg viewBox="0 0 24 24" fill="white" width="16" height="16"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>',
        'star': '<svg viewBox="0 0 24 24" fill="white" width="16" height="16"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>',
        'home': '<svg viewBox="0 0 24 24" fill="white" width="16" height="16"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>',
        'pin': '<svg viewBox="0 0 24 24" fill="white" width="16" height="16"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>',
        'ring': '<svg viewBox="0 0 24 24" fill="white" width="16" height="16"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/></svg>'
      };
      
      // Use current selected color
      const color = this.currentColor || '#E74C3C';
      const iconSVG = iconSVGs[this.currentIcon] || '';
      
      if (this.currentIcon === 'none' || !iconSVG) {
        // No icon selected = no marker on map
        this.marker = null;
        return;
      } else {
        // Custom HTML marker with icon
        const el = document.createElement('div');
        el.className = 'homepage-customizer__custom-marker';
        el.innerHTML = `
          <div style="
            width: 36px;
            height: 36px;
            background: ${color};
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          ">
            <div style="transform: rotate(45deg);">
              ${iconSVG}
            </div>
          </div>
        `;
        
        this.marker = new mapboxgl.Marker({ element: el, anchor: 'bottom', draggable: true })
          .setLngLat([this.currentLocation.lng, this.currentLocation.lat])
          .addTo(this.map);

        // Update icon position after drag
        this.marker.on('dragend', () => {
          const lngLat = this.marker.getLngLat();
          this.markerOffset = {
            lng: lngLat.lng - this.currentLocation.lng,
            lat: lngLat.lat - this.currentLocation.lat
          };
        });
      }
    },

    /**
     * Toggle color selector visibility based on icon selection.
     * Color is only relevant when an icon is selected (it colors the marker).
     */
    toggleColorVisibility() {
      if (!this.colorGroup) return;
      if (this.currentIcon === 'none') {
        this.colorGroup.classList.add('is-hidden');
      } else {
        this.colorGroup.classList.remove('is-hidden');
      }
    },

    /**
     * Listen for location updates from hero section
     */
    listenForHeroLocation() {
      // Custom event from hero section
      document.addEventListener('prnts:selectLocation', (e) => {
        const { lat, lng, name, zoom } = e.detail;
        
        if (lat && lng) {
          this.currentLocation = { lat, lng, name: name || '' };
          
          // Update map
          if (this.map) {
            this.map.flyTo({
              center: [lng, lat],
              zoom: zoom || 13,
              duration: 1500
            });
          }
          
          // Update marker
          if (this.marker) {
            this.marker.setLngLat([lng + this.markerOffset.lng, lat + this.markerOffset.lat]);
          }
          
          // Update inputs
          if (this.elements.searchInput) {
            this.elements.searchInput.value = name || '';
          }
          
          if (this.elements.textInput) {
            this.elements.textInput.value = (name || '').toUpperCase();
          }
          
          // Update poster text
          this.updatePosterText();
          
          this.updateFormInputs();
          
          // Scroll to customizer section
          const section = document.getElementById('ontwerp-je-poster');
          if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    },

    /**
     * Search for location
     */
    async searchLocation(query) {
      if (!query || query.length < 2) {
        this.elements.searchResults.hidden = true;
        return;
      }

      const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`;
      const params = new URLSearchParams({
        access_token: this.accessToken,
        limit: 5,
        language: 'nl',
        types: 'place,locality,neighborhood,address'
      });

      try {
        const response = await fetch(`${endpoint}?${params}`);
        const data = await response.json();
        
        if (data.features && data.features.length > 0) {
          this.showSearchResults(data.features);
        } else {
          this.elements.searchResults.hidden = true;
        }
      } catch (error) {
        console.error('Geocoding error:', error);
      }
    },

    /**
     * Extract country name from a Mapbox feature's context array
     */
    extractCountry(feature) {
      if (feature.context) {
        const countryCtx = feature.context.find(c => c.id && c.id.startsWith('country'));
        if (countryCtx) return countryCtx.text;
      }
      // Fallback: last part of place_name (e.g. "Amsterdam, Noord-Holland, Netherlands")
      if (feature.place_name) {
        const parts = feature.place_name.split(',');
        if (parts.length >= 2) return parts[parts.length - 1].trim();
      }
      return null;
    },

    /**
     * Update the country display on poster and input
     */
    setCountry(countryName) {
      if (!countryName) return;
      const upper = countryName.toUpperCase();
      this.currentCountry = upper;
      if (this.elements.countryInput) {
        this.elements.countryInput.value = upper;
      }
      if (this.elements.posterCountry) {
        this.elements.posterCountry.textContent = `— ${upper} —`;
      }
    },

    /**
     * Show search results
     */
    showSearchResults(features) {
      this.elements.searchResults.innerHTML = '';
      this.elements.searchResults.hidden = false;

      features.forEach(feature => {
        const div = document.createElement('div');
        div.className = 'homepage-customizer__search-result';
        div.textContent = feature.place_name;
        
        div.addEventListener('click', () => {
          const [lng, lat] = feature.center;
          const name = feature.text || feature.place_name.split(',')[0];

          this.currentLocation = { lat, lng, name };

          // Update UI
          this.elements.searchInput.value = feature.place_name;
          this.elements.searchResults.hidden = true;

          if (this.elements.textInput) {
            this.elements.textInput.value = name.toUpperCase();
          }

          // Extract and set country from feature
          const country = this.extractCountry(feature);
          if (country) this.setCountry(country);

          // Update poster text
          this.updatePosterText();
          
          // Update map
          if (this.map) {
            this.map.flyTo({ center: [lng, lat], zoom: 13 });
          }
          if (this.marker) {
            this.marker.setLngLat([lng + this.markerOffset.lng, lat + this.markerOffset.lat]);
          }
          
          this.updateFormInputs();
        });
        
        this.elements.searchResults.appendChild(div);
      });
    },

    /**
     * Set location from map click
     */
    setLocation(lng, lat) {
      this.currentLocation.lat = lat;
      this.currentLocation.lng = lng;
      
      if (this.marker) {
        this.marker.setLngLat([lng + this.markerOffset.lng, lat + this.markerOffset.lat]);
      }

      this.updateFormInputs();
    },

    /**
     * Reverse geocode coordinates to get place name
     */
    async reverseGeocode(lng, lat) {
      const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json`;
      const params = new URLSearchParams({
        access_token: this.accessToken,
        limit: 1,
        language: 'nl',
        types: 'place,locality,neighborhood'
      });

      try {
        const response = await fetch(`${endpoint}?${params}`);
        const data = await response.json();

        if (data.features && data.features.length > 0) {
          const feature = data.features[0];
          const name = feature.text || feature.place_name.split(',')[0];
          this.currentLocation.name = name;

          if (this.elements.searchInput) {
            this.elements.searchInput.value = feature.place_name;
          }
          if (this.elements.textInput) {
            this.elements.textInput.value = name.toUpperCase();
          }

          // Extract and set country from feature
          const country = this.extractCountry(feature);
          if (country) this.setCountry(country);

          // Update poster text
          this.updatePosterText();

          this.updateFormInputs();
        }
      } catch (error) {
        console.error('Reverse geocoding error:', error);
      }
    },

    /**
     * Update hidden form inputs
     */
    updateFormInputs() {
      const { lat, lng, name } = this.currentLocation;
      const text = this.elements.textInput?.value || name.toUpperCase();
      const styleName = this.getStyleName(this.currentStyle);

      if (this.elements.locationInput) {
        this.elements.locationInput.value = name;
      }
      if (this.elements.coordsInput) {
        this.elements.coordsInput.value = `${lat.toFixed(6)},${lng.toFixed(6)}`;
      }
      if (this.elements.textValueInput) {
        this.elements.textValueInput.value = text;
      }
      if (this.elements.styleValueInput) {
        this.elements.styleValueInput.value = styleName;
      }
      if (this.elements.formatValueInput) {
        this.elements.formatValueInput.value = this.currentFormat;
      }

      // Icon value
      if (this.elements.iconValueInput) {
        const iconLabels = {
          'none': '',
          'heart': 'Hartje',
          'home': 'Thuis',
          'star': 'Ster',
          'baby': 'Baby',
          'ring': 'Verloving',
          'cake': 'Verjaardag',
          'airplane': 'Reis',
          'coffee': 'Eerste date',
          'graduation': 'Afstuderen',
          'pawprint': 'Huisdier',
          'music': 'Concert',
          'pin': 'Pin'
        };
        this.elements.iconValueInput.value = iconLabels[this.currentIcon] || '';
      }

      // Color value
      if (this.elements.colorValueInput) {
        const colorLabels = {
          '#E74C3C': 'Rood',
          '#1A1A1A': 'Zwart',
          '#3498DB': 'Blauw',
          '#27AE60': 'Groen',
          '#F39C12': 'Oranje',
          '#9B59B6': 'Paars'
        };
        this.elements.colorValueInput.value = colorLabels[this.currentColor] || this.currentColor;
      }

      // Orientation value
      if (this.elements.orientationValueInput) {
        const orientationLabels = {
          'portrait': 'Staand',
          'landscape': 'Liggend'
        };
        this.elements.orientationValueInput.value = orientationLabels[this.currentOrientation] || this.currentOrientation;
      }

      // Frame/Lijst value
      if (this.elements.frameValueInput) {
        this.elements.frameValueInput.value = this.currentFrame || 'Geen';
      }

      // Note: _customization_data is built in the submit handler, not here.
      // The submit handler builds the complete JSON that the PDF generator reads.
    },

    /**
     * Generate preview URL for print
     */
    generatePreviewUrl() {
      if (!this.currentLocation || !this.accessToken) return '';

      const { lat, lng } = this.currentLocation;
      const styleId = this.currentStyle.split('/').pop();
      const zoom = Math.floor(this.map?.getZoom() || 13);

      // Mapbox Static Images API - use sander1971 custom styles
      const previewUrl = `https://api.mapbox.com/styles/v1/sander1971/${styleId}/static/${lng},${lat},${zoom}/1280x720@2x?access_token=${this.accessToken}`;

      if (this.elements.previewUrlInput) {
        this.elements.previewUrlInput.value = previewUrl;
      }

      return previewUrl;
    },

    /**
     * Capture the full poster as a high-res PNG.
     * Draws map canvas + fade + text + icon onto an offscreen canvas.
     */
    async capturePosterImage() {
      if (!this.map) return this.generatePreviewUrl();

      try {
        // Wait for map to be fully loaded
        if (!this.map.loaded()) {
          await new Promise(resolve => {
            this.map.once('idle', resolve);
            setTimeout(resolve, 3000);
          });
        }

        const mapCanvas = this.map.getCanvas();
        const mapDataUrl = mapCanvas.toDataURL('image/png');
        if (!mapDataUrl || mapDataUrl.length < 100) throw new Error('Empty canvas');

        // Get poster dimensions from current format/orientation
        const p = this.PDF_PROPORTIONS;
        const scale = 1;
        const baseW = 600;
        const format = this.currentFormat || '30x40';
        const orient = this.currentOrientation || 'portrait';
        const ratioStr = (this.formatAspectRatios[format] || {})[orient] || '3 / 4';
        const parts = ratioStr.split('/').map(s => parseFloat(s.trim()));
        const aspect = parts[0] / parts[1];
        const W = Math.round(baseW * scale);
        const H = Math.round((baseW / aspect) * scale);

        // Layout
        const textH = Math.round(H * p.textRatio);
        const mapH = H - textH;

        // Create offscreen canvas
        const c = document.createElement('canvas');
        c.width = W;
        c.height = H;
        const ctx = c.getContext('2d');

        // White background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, W, H);

        // Draw map
        const mapImg = await this._loadImage(mapDataUrl);
        ctx.drawImage(mapImg, 0, 0, W, mapH);

        // Fade gradient (bottom of map → white)
        const fadeH = mapH * p.fadeRatio;
        const fadeY = mapH - fadeH;
        const grad = ctx.createLinearGradient(0, fadeY, 0, mapH);
        grad.addColorStop(0, 'rgba(255,255,255,0)');
        grad.addColorStop(0.2, 'rgba(255,255,255,0.15)');
        grad.addColorStop(0.4, 'rgba(255,255,255,0.4)');
        grad.addColorStop(0.6, 'rgba(255,255,255,0.7)');
        grad.addColorStop(0.8, 'rgba(255,255,255,0.9)');
        grad.addColorStop(1, 'rgba(255,255,255,1)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, fadeY, W, fadeH);

        // Draw icon/marker on map
        if (this.currentIcon && this.currentIcon !== 'none') {
          const markerEl = this.container?.querySelector('.homepage-customizer__custom-marker');
          if (markerEl) {
            // Get marker position relative to map
            const mapEl = this.elements.mapContainer;
            const mapRect = mapEl.getBoundingClientRect();
            const markerRect = markerEl.getBoundingClientRect();
            const relX = (markerRect.left + markerRect.width / 2 - mapRect.left) / mapRect.width;
            const relY = (markerRect.top + markerRect.height / 2 - mapRect.top) / mapRect.height;

            // Draw colored circle marker
            const mx = relX * W;
            const my = relY * mapH;
            const mRadius = W * 0.02;
            ctx.beginPath();
            ctx.arc(mx, my, mRadius, 0, Math.PI * 2);
            ctx.fillStyle = this.currentColor || '#E74C3C';
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = mRadius * 0.3;
            ctx.stroke();

            // Draw the icon SVG inside
            const svg = markerEl.querySelector('svg');
            if (svg) {
              try {
                const svgData = new XMLSerializer().serializeToString(svg);
                const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                const svgUrl = URL.createObjectURL(svgBlob);
                const svgImg = await this._loadImage(svgUrl);
                const iconSize = mRadius * 1.2;
                ctx.drawImage(svgImg, mx - iconSize / 2, my - iconSize / 2, iconSize, iconSize);
                URL.revokeObjectURL(svgUrl);
              } catch (e) { /* icon drawing optional */ }
            }
          }
        }

        // Text area
        const centerX = W / 2;
        const textName = (this.elements.textInput?.value || 'AMSTERDAM').toUpperCase();
        const country = (this.elements.countryInput?.value || 'NEDERLAND').toUpperCase();
        const { lat, lng } = this.currentLocation;
        const latD = Math.floor(Math.abs(lat));
        const latM = Math.floor((Math.abs(lat) - latD) * 60);
        const lngD = Math.floor(Math.abs(lng));
        const lngM = Math.floor((Math.abs(lng) - lngD) * 60);
        const coords = `${latD}°${String(latM).padStart(2,'0')}'${lat>=0?'N':'S'} ${lngD}°${String(lngM).padStart(2,'0')}'${lng>=0?'E':'W'}`;

        const titleSize = Math.round(textH * p.titleFsRatio);
        const countrySize = Math.round(textH * p.countryFsRatio);
        const coordsSize = Math.round(textH * p.coordsFsRatio);

        const padTop = textH * 0.16;
        const titleY = mapH + padTop + textH * 0.18;
        const countryY = titleY + textH * 0.22;
        const coordsY = countryY + textH * 0.18;

        ctx.textAlign = 'center';

        // Title
        ctx.fillStyle = '#1a1a1a';
        ctx.font = `bold ${titleSize}px Georgia, "Times New Roman", serif`;
        ctx.letterSpacing = `${titleSize * 0.15}px`;
        ctx.fillText(textName, centerX, titleY);

        // Country
        ctx.font = `${countrySize}px Georgia, "Times New Roman", serif`;
        ctx.letterSpacing = `${countrySize * 0.3}px`;
        ctx.fillText(`— ${country} —`, centerX, countryY);

        // Coordinates
        ctx.fillStyle = '#666666';
        ctx.font = `${coordsSize}px Georgia, "Times New Roman", serif`;
        ctx.letterSpacing = `${coordsSize * 0.1}px`;
        ctx.fillText(coords, centerX, coordsY);

        const dataUrl = c.toDataURL('image/jpeg', 0.7);

        if (this.elements.previewUrlInput) {
          this.elements.previewUrlInput.value = dataUrl;
        }

        console.log('[PRNTS] Poster captured successfully:', Math.round(dataUrl.length / 1024) + 'KB');
        return dataUrl;
      } catch (err) {
        console.warn('[PRNTS] Poster capture failed, falling back to Mapbox static:', err);
        return this.generatePreviewUrl();
      }
    },

    /** Helper: load image from URL/dataURL */
    _loadImage(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    },

    /**
     * Get human-readable style name
     */
    getStyleName(styleUrl) {
      const styleMap = {
        'clig9f069007501pf5qmi5tkr': 'Klassiek',
        'clig9eisy007r01pad097dsuv': 'Water',
        'cligaqjz6006t01pgaa2k9mlj': 'Donker'
      };
      const styleId = styleUrl.split('/').pop();
      return styleMap[styleId] || 'Klassiek';
    },

    /**
     * Validate form data before submission
     * @returns {Object} { valid: boolean, errors: string[] }
     */
    validateFormData() {
      const errors = [];
      const { lat, lng, name } = this.currentLocation;

      // 1. Validate variant ID
      const variantId = this.elements.variantInput?.value;
      if (!variantId || variantId === '' || variantId === '0' || variantId === 'undefined' || variantId === 'null') {
        errors.push('Geen geldig formaat geselecteerd');
      }

      // 2. Validate coordinates are numbers
      if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
        errors.push('Ongeldige locatie coördinaten');
      }

      // 3. Validate coordinates range
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        errors.push('Coördinaten buiten geldig bereik');
      }

      // 4. Validate location name
      if (!name || name.trim() === '') {
        errors.push('Geen locatie geselecteerd');
      }

      // 5. Validate format selection
      const selectedFormat = document.querySelector('.homepage-customizer__format.is-selected');
      if (!selectedFormat) {
        errors.push('Selecteer een formaat');
      }

      const result = { valid: errors.length === 0, errors };
      return result;
    },

    /**
     * Show error message to user
     * @param {string} message
     */
    showError(message) {
      // Use global Toast if available, otherwise alert
      if (window.PRNTS?.Toast) {
        window.PRNTS.Toast.error(message, 'Kan niet toevoegen');
      } else {
        alert(message);
      }

      // Highlight submit button as error state
      const submitBtn = this.elements.form?.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.classList.add('is-error');
        setTimeout(() => submitBtn.classList.remove('is-error'), 3000);
      }
    },

    /**
     * Open cart drawer after adding item
     */
    async openCartDrawer() {

      // ALWAYS refresh ourselves - don't rely on theme.js which may have broken drawer reference
      await this.refreshCartDrawer();

      // Open drawer directly
      const cartDrawer = document.querySelector('[data-cart-drawer]');
      if (cartDrawer) {
        cartDrawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('cart-drawer-open', 'overflow-hidden');
      } else {
        window.location.href = '/cart';
      }
    },

    /**
     * Refresh cart drawer contents via AJAX (robust version with fallbacks)
     */
    async refreshCartDrawer() {

      try {
        // Fetch fresh cart drawer HTML from Shopify Section Rendering API
        // Include preview_theme_id if we're previewing an unpublished theme
        let sectionUrl = '/?section_id=cart-drawer';
        const urlParams = new URLSearchParams(window.location.search);
        const previewThemeId = urlParams.get('preview_theme_id');
        if (previewThemeId) {
          sectionUrl += '&preview_theme_id=' + previewThemeId;
        }

        const response = await fetch(sectionUrl, {
          headers: { 'Accept': 'text/html' }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const html = await response.text();

        // Find current cart drawer on page
        const currentDrawer = document.querySelector('[data-cart-drawer]');
        if (!currentDrawer) {
          return;
        }

        // Parse fetched HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Find content in fetched HTML - try multiple selectors
        const newContent = doc.querySelector('[data-cart-drawer-content]')
                        || doc.querySelector('.cart-drawer__content');

        // Find content in current drawer
        const currentContent = currentDrawer.querySelector('[data-cart-drawer-content]')
                            || currentDrawer.querySelector('.cart-drawer__content');

        if (newContent && currentContent) {
          currentContent.innerHTML = newContent.innerHTML;
        } else {
          const newDrawer = doc.querySelector('[data-cart-drawer]');
          if (newDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
          }
        }

        // Fetch cart data for count update + property injection
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();

        // JS-based property injection: ensure properties are visible in drawer
        // This works regardless of Liquid template rendering
        const drawerItems = document.querySelectorAll('.cart-drawer__item[data-line-key]');

        drawerItems.forEach(itemEl => {
          const lineKey = itemEl.getAttribute('data-line-key');
          // Match cart.js item by key
          const cartItem = cartData.items.find(ci => ci.key === lineKey);
          if (!cartItem) {
            return;
          }

          // Check if properties are already rendered by Liquid
          const existingPropsEl = itemEl.querySelector('.cart-drawer__item-properties');
          const existingPropCount = existingPropsEl ? existingPropsEl.querySelectorAll('.cart-drawer__item-property').length : 0;

          // Get visible properties from cart data
          const props = cartItem.properties || {};
          const visibleProps = Object.entries(props).filter(([k, v]) => !k.startsWith('_') && v && v !== '');

          // If Liquid didn't render properties (or rendered fewer), inject them via JS
          if (visibleProps.length > 0 && existingPropCount === 0) {

            // Create or reuse properties container
            let propsContainer = existingPropsEl;
            if (!propsContainer) {
              propsContainer = document.createElement('div');
              propsContainer.className = 'cart-drawer__item-properties';
              // Insert after variant line, before price
              const variantEl = itemEl.querySelector('.cart-drawer__item-variant');
              const detailsEl = itemEl.querySelector('.cart-drawer__item-details');
              if (variantEl && variantEl.nextSibling) {
                detailsEl.insertBefore(propsContainer, variantEl.nextSibling);
              } else if (detailsEl) {
                const priceEl = itemEl.querySelector('.cart-drawer__item-price');
                if (priceEl) {
                  detailsEl.insertBefore(propsContainer, priceEl);
                } else {
                  detailsEl.appendChild(propsContainer);
                }
              }
            }

            // Clear and populate
            propsContainer.innerHTML = '';
            visibleProps.forEach(([key, value]) => {
              const p = document.createElement('p');
              p.className = 'cart-drawer__item-property';
              const span = document.createElement('span');
              span.textContent = key + ':';
              p.appendChild(span);
              p.appendChild(document.createTextNode(' ' + value));
              propsContainer.appendChild(p);
            });

          }
        });

        // Update cart count in header
        const countEls = document.querySelectorAll('[data-cart-count]');
        countEls.forEach(el => {
          el.textContent = cartData.item_count;
          if (cartData.item_count > 0) {
            el.classList.remove('is-hidden');
          } else {
            el.classList.add('is-hidden');
          }
        });

      } catch (error) {
      }

    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => HomepageCustomizer.init());
  } else {
    HomepageCustomizer.init();
  }

})();
